import { gql } from '@apollo/client'
import { Typography } from '@material-ui/core'
import React, { PropsWithChildren } from 'react'
import styled from 'styled-components'
import { Breadcrumb, Breadcrumbs } from '../../../components/breadcrumbs/Breadcrumbs'
import { SecondaryBanner } from '../../../components/secondarybanner/SecondaryBanner'
import { rem } from '../../../utils'
import { isNotNull } from '../../../utils/nonNull'
import { GlossaryBannerFragment } from './__generated__/glossaryBanner'

export type SecondaryPageBannerProps = {
  banner: GlossaryBannerFragment
}

const diagonalSizeMobile = 32 as const
const diagonalSizeDesktop = 93 as const

const StyledSecondaryBanner = styled(SecondaryBanner)<{ breadcrumbs: React.ReactNode }>`
  clip-path: polygon(0 0, 100% 0, 100% calc(100% - ${props => rem(diagonalSizeMobile, props.theme)}), 0 100%);
  padding-bottom: ${props => rem(74, props.theme)};

  > .MuiContainer-root {
    height: 100%;

    > .MuiGrid-container {
      height: 100%;

      > .MuiGrid-item {
        display: flex;
        flex-direction: column;
      }
    }
  }

  ${props => props.theme.breakpoints.up('md')} {
    padding-bottom: 0;
    clip-path: polygon(0 0, 100% 0, 100% calc(100% - ${props => rem(diagonalSizeDesktop, props.theme)}), 0 100%);

    .MuiBreadcrumbs-ol {
      margin-top: ${props => rem(20, props.theme)};
      max-width: ${props => rem(350, props.theme)};
      flex-wrap: nowrap;

      ${props => props.theme.breakpoints.up('md')} {
        max-width: ${props => rem(410, props.theme)};
      }

      .MuiBreadcrumbs-li {
        flex: 0 0 auto;
        white-space: nowrap;

        &:last-child {
          flex: 0 1 auto;
          overflow: hidden;

          > a {
            max-width: 100%;

            p {
              max-width: 100%;
              overflow: hidden;
              white-space: nowrap;
              text-overflow: ellipsis;
            }
          }
        }

        &:focus-within {
          outline: ${props => props.theme.typography.pxToRem(1)} auto -webkit-focus-ring-color;
        }
      }
    }
  }
`

const StyledWrapper = styled.div`
  position: relative;

  &::after {
    content: "";
    position: absolute;
    right: 0;
    bottom: -1px;
    left: 0;
    background: #f2f2f2;
    z-index: -1;
    clip-path: polygon(100% 0, 0 100%, 100% 100%);
    height: ${props => rem(diagonalSizeMobile, props.theme)};

    ${props => props.theme.breakpoints.up('sm')} {
      height: ${props => rem(diagonalSizeDesktop, props.theme)};
    }
  }
`

const StyledSubtitle = styled(Typography)`
  font-size: ${props => props.theme.typography.pxToRem(24)};
  font-weight: 500;
  color: ${props => props.theme.palette.primary.dark};
  margin-bottom: ${props => props.theme.typography.pxToRem(5)};
  margin-top: ${props => props.theme.typography.pxToRem(42)};

  ${props => props.theme.breakpoints.up('sm')} {
    margin-top: unset;
  }
`

const StyledTitle = styled(Typography)`
  font-size: ${props => props.theme.typography.pxToRem(40)};
  font-weight: 500;
  color: ${props => props.theme.palette.primary.dark};
`

export const SecondaryPageBanner: React.FC<PropsWithChildren<SecondaryPageBannerProps>> = ({ banner }) => {
  const { subTitle, title, primaryButton, secondaryButton } = banner

  return (
    <StyledWrapper>
      <StyledSecondaryBanner image glossary={true} breadcrumbs={(              
        <Breadcrumbs>
          <Breadcrumb href={'/'} home text={'Home'} />
          <Breadcrumb href={'/tools'} text={'Tools'} />
          <Breadcrumb href={'/glossary'} text={'Glossary'} />
        </Breadcrumbs>)} src={undefined}>
        {subTitle && (
          <StyledSubtitle>{subTitle}</StyledSubtitle>
        )}
        {title && (
          <StyledTitle variant="h1">{title}</StyledTitle>
        )}
        {(primaryButton || secondaryButton) && (
          <SecondaryBanner section glossary={true}>
            {(primaryButton && primaryButton.__typename === 'linkField_Link' && isNotNull(primaryButton.url)) && (
              <SecondaryBanner cta buttonProps={{ primary: true, buttonColor: 'orange', target: primaryButton?.target === '_blank' ? '_blank' : '_self' }} href={primaryButton.url?.replace('cms.', '') || '#'}>
                {primaryButton.customText}
              </SecondaryBanner>
            )}
            {(secondaryButton && secondaryButton.__typename === 'linkField_Link' && isNotNull(secondaryButton.url)) &&
              <SecondaryBanner cta buttonProps={{ primary: true, buttonColor: 'black', target: secondaryButton?.target === '_blank' ? '_blank' : '_self', size: 'small' }} href={secondaryButton.url?.replace('cms.', '') || '#'}>
                {secondaryButton.customText}
              </SecondaryBanner>
            }
          </SecondaryBanner>
        )}
      </StyledSecondaryBanner>
    </StyledWrapper>
  )
}

export const GLOSSARY_BANNER_FRAGMENT = gql`
  fragment GlossaryBannerFragment on glossaryBanner_BlockType {
    title: bannerTitle
    subTitle: bannerSubtitle
    primaryButton: primaryBannerButton {
      url
      customText
      target
    }
    secondaryButton: secondaryBannerButton {
      url
      customText
      target
    }
  }
`
